import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts'
import { GraphContainer, GraphTitle, GraphExpandButton, GraphWrapper } from '../../CardStyles'
import { GraphProps } from '../../../utils/types'
import { formatYAxisBandwidth, prepareGraphData } from '../../../utils/graphs'
import { DOWNLOAD_COLOR, UPLOAD_COLOR, axisStyle } from '../../../utils/constants'
import BandwidthTooltip from './tooltips/BandwidthTooltip'

const BandwidthGraph: React.FC<GraphProps> = ({ data, legendData, isExpanded, onExpand, hideTitle = false }) => {
  const { t, i18n } = useTranslation()
  
  const { filteredDisplayData, xAxisTicks } = useMemo(() => 
    prepareGraphData(data, legendData, isExpanded, ['download', 'upload']), 
    [data, legendData, isExpanded, i18n.language]
  );

  if (filteredDisplayData.length === 0) {
    return (
      <GraphContainer sx={{ height: isExpanded ? 465 : 'auto' }}>
        {!hideTitle && (
          <GraphTitle>
            <Typography variant="subtitle2">{t('wan.graphs.bandwidth.title')}</Typography>
            {!isExpanded && (
              <GraphExpandButton onClick={onExpand}>
                <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
              </GraphExpandButton>
            )}
          </GraphTitle>
        )}
        <Box sx={{ 
          height: isExpanded ? '100%' : 100, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
          >
            {t('wan.graphs.no_data')}
          </Typography>
        </Box>
      </GraphContainer>
    )
  }  

  return (
    <GraphContainer sx={{ height: isExpanded ? 465 : 'auto' }}>
      {!hideTitle && (
        <GraphTitle>
          <Typography variant="subtitle2">{t('wan.graphs.bandwidth.title')}</Typography>
          {!isExpanded && (
            <GraphExpandButton onClick={onExpand}>
              <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
            </GraphExpandButton>
          )}
        </GraphTitle>
      )}
      <GraphWrapper>
        <ResponsiveContainer width="100%" height={isExpanded ? 400 : 200}>
          <LineChart
            data={filteredDisplayData}
            margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="simplifiedTime"
              tick={{ ...axisStyle }}
              ticks={xAxisTicks}
            />
            <YAxis
              tickFormatter={formatYAxisBandwidth}
              tick={{ ...axisStyle }}
            />
            <RechartsTooltip content={<BandwidthTooltip />} />
            <Line
              type="monotone"
              dataKey="download"
              name={t('wan.graphs.bandwidth.download')}
              stroke={DOWNLOAD_COLOR}
              strokeWidth={isExpanded ? 2 : 1}
              dot={false}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="upload"
              name={t('wan.graphs.bandwidth.upload')}
              stroke={UPLOAD_COLOR}
              strokeWidth={isExpanded ? 2 : 1}
              dot={false}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2, 
          mt: 1, 
          mr: 3,
          fontSize: 10
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 13, 
              height: 13, 
              borderRadius: '50%', 
              backgroundColor: DOWNLOAD_COLOR, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <i className="fa-solid fa-arrow-down" style={{ color: 'white', fontSize: 9 }} />
            </Box>
            <Typography sx={{ fontSize: 10, color: '#666' }}>
              {t('wan.graphs.bandwidth.descendant')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 13, 
              height: 13, 
              borderRadius: '50%', 
              backgroundColor: UPLOAD_COLOR, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <i className="fa-solid fa-arrow-up" style={{ color: 'white', fontSize: 9 }} />
            </Box>
            <Typography sx={{ fontSize: 10, color: '#666' }}>
              {t('wan.graphs.bandwidth.ascendant')}
            </Typography>
          </Box>
        </Box>
      </GraphWrapper>
    </GraphContainer>
  )
}

export default BandwidthGraph
